import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CreatividadNombre from "./CreatividadNombre";
import { downloadInformePdf } from "../utils/downloadInforme";
import { getCreatividadLabels } from "../utils/creatividadesLabels";
import styles from "../styles/encuesta4c.module.css";

const CREATIVIDAD_COLORS = [
  { bar: "#e1147e", soft: "rgba(225, 20, 126, 0.12)", ring: "rgba(225, 20, 126, 0.35)" },
  { bar: "#6366f1", soft: "rgba(99, 102, 241, 0.12)", ring: "rgba(99, 102, 241, 0.35)" },
  { bar: "#0ea5e9", soft: "rgba(14, 165, 233, 0.12)", ring: "rgba(14, 165, 233, 0.35)" },
  { bar: "#10b981", soft: "rgba(16, 185, 129, 0.12)", ring: "rgba(16, 185, 129, 0.35)" },
];

function tienePercentil(c) {
  return c != null && Number.isFinite(Number(c.percentil));
}

function pickPrincipal(creatividades, isFreemium) {
  if (!creatividades.length) return null;
  if (isFreemium && creatividades.some(tienePercentil)) {
    return [...creatividades].sort(
      (a, b) =>
        (Number(b.percentil) || 0) - (Number(a.percentil) || 0) ||
        b.porcentaje - a.porcentaje ||
        b.puntos - a.puntos
    )[0];
  }
  return creatividades.find((c) => c.es_principal) ?? creatividades[0];
}

function PerfilSkeleton({ isFreemium }) {
  const { t } = useTranslation();
  return (
    <section
      className={styles.heroSection}
      aria-busy="true"
      aria-live="polite"
      aria-label={t("results.skeletonAria")}
    >
      <p className={styles.skeletonStatus}>{t("results.skeletonStatus")}</p>
      <div className={`${styles.heroCard} ${styles.skeletonCard}`}>
        <div className={styles.heroBadge}>{t("results.mainBadge")}</div>
        <div className={styles.heroContent}>
          <div className={styles.heroLeft}>
            <span className={`${styles.skeletonBone} ${styles.skeletonName}`} />
            <span className={`${styles.skeletonBone} ${styles.skeletonLegacy}`} />
            <span className={`${styles.skeletonBone} ${styles.skeletonDesc}`} />
            <span className={`${styles.skeletonBone} ${styles.skeletonDescShort}`} />
          </div>
          <div className={styles.heroPctWrap}>
            <span className={`${styles.skeletonBone} ${styles.skeletonPct}`} />
            <span className={`${styles.skeletonBone} ${styles.skeletonPctLabel}`} />
          </div>
        </div>
        <div className={styles.heroBarBg}>
          <div className={`${styles.heroBarFill} ${styles.skeletonBarFill}`} />
        </div>
      </div>
      {!isFreemium && (
        <div className={styles.skeletonGrid} aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className={styles.skeletonMiniCard}>
              <div className={styles.creatCardHeader}>
                <span className={`${styles.skeletonBone} ${styles.skeletonMiniName}`} />
                <span className={`${styles.skeletonBone} ${styles.skeletonMiniPct}`} />
              </div>
              <div className={styles.creatCardBarBg}>
                <div className={`${styles.creatCardBarFill} ${styles.skeletonMiniBar}`} />
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function creatividadDesc(creatividad, t, language) {
  const labels = getCreatividadLabels(creatividad, t);
  const apiDesc = creatividad?.descripcion;
  const lng = String(language || "es").slice(0, 2);
  if (apiDesc && lng === "es") return apiDesc;
  return labels.desc || apiDesc || "";
}

function ResultadosEncuesta({ ids, perfil, loadingPerfil }) {
  const { t, i18n } = useTranslation();
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const isFreemium = ids?.tipo === "freemium";

  const creatividades = [...(perfil?.creatividades || [])].sort(
    (a, b) => b.porcentaje - a.porcentaje || b.puntos - a.puntos
  );
  const tienePerfil = creatividades.some((c) => (c.puntos ?? 0) > 0);
  const principal = tienePerfil ? pickPrincipal(creatividades, isFreemium) : null;
  const restantes = isFreemium ? [] : creatividades.filter((c) => c !== principal);
  const usaPercentil = isFreemium && tienePercentil(principal);
  const valorBarra = usaPercentil ? Number(principal.percentil) : principal?.porcentaje;
  const etiquetaValor = usaPercentil
    ? `P${Math.round(Number(principal.percentil))}`
    : `${principal?.porcentaje}%`;
  const colorFor = (index, isMain) =>
    CREATIVIDAD_COLORS[isMain ? 0 : index % CREATIVIDAD_COLORS.length];

  const handleDownload = useCallback(async () => {
    if (!ids?.id_usuario || !ids?.id_encuesta) return;
    setDownloading(true);
    setDownloadError(null);
    const name = ids.nombre_usuario || "4C";
    try {
      await downloadInformePdf({
        id_encuesta: ids.id_encuesta,
        id_usuario: ids.id_usuario,
        nombreArchivoFallback: isFreemium
          ? t("results.fileFree", { name })
          : t("results.fileFull", { name }),
        errorMessage: t("errors.downloadGeneric"),
      });
    } catch (err) {
      setDownloadError(err.message || t("errors.downloadFailed"));
    } finally {
      setDownloading(false);
    }
  }, [ids, isFreemium, t]);

  return (
    <div
      className={`${styles.stepTransition} ${styles.resultsWrap}`}
      role="region"
      aria-label={t("results.aria")}
    >
      <div className={styles.resultsHeader}>
        <div className={styles.thanksIconWrap}>
          <svg
            className={styles.thanksIcon}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h2 className={styles.thanksTitle}>{t("results.thanks")}</h2>
        <p className={styles.resultsEyebrow}>{t("results.eyebrow")}</p>
      </div>

      <div className={styles.resultsActions}>
        {downloadError && (
          <p className={styles.resultsActionError}>{downloadError}</p>
        )}
        <button
          type="button"
          className={styles.btnDownload}
          onClick={handleDownload}
          disabled={downloading || loadingPerfil}
        >
          <svg
            className={styles.btnDownloadIcon}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
            />
          </svg>
          {downloading
            ? t("results.generating")
            : isFreemium
              ? t("results.downloadFree")
              : t("results.download")}
        </button>
        <p className={styles.resultsEmailNote}>
          {isFreemium ? t("results.noteFree") : t("results.noteEmail")}
        </p>
      </div>

      {loadingPerfil ? (
        <PerfilSkeleton isFreemium={isFreemium} />
      ) : !tienePerfil ? (
        <p className={styles.resultsEmpty}>{t("results.empty")}</p>
      ) : (
        <>
          {principal && (
            <section className={styles.heroSection}>
              <div
                className={styles.heroCard}
                style={{
                  borderColor: colorFor(0, true).ring,
                  background: `linear-gradient(135deg, ${colorFor(0, true).soft} 0%, white 55%)`,
                }}
              >
                <div className={styles.heroBadge}>{t("results.mainBadge")}</div>
                <div className={styles.heroContent}>
                  <div className={styles.heroLeft}>
                    <CreatividadNombre creatividad={principal} variant="hero" />
                    {creatividadDesc(principal, t, i18n.language) && (
                      <p className={styles.heroDesc}>
                        {creatividadDesc(principal, t, i18n.language)}
                      </p>
                    )}
                  </div>
                  <div className={styles.heroPctWrap}>
                    <span
                      className={styles.heroPct}
                      style={{ color: colorFor(0, true).bar }}
                    >
                      {etiquetaValor}
                    </span>
                    <span className={styles.heroPctLabel}>
                      {usaPercentil ? t("results.percentile") : t("results.ofProfile")}
                    </span>
                  </div>
                </div>
                <div className={styles.heroBarBg}>
                  <div
                    className={styles.heroBarFill}
                    style={{
                      width: `${valorBarra}%`,
                      backgroundColor: colorFor(0, true).bar,
                    }}
                  />
                </div>
              </div>
            </section>
          )}

          {restantes.length > 0 && (
            <section className={styles.gridSection}>
              <h4 className={styles.gridTitle}>{t("results.restTitle")}</h4>
              <div className={styles.creatividadesGrid}>
                {restantes.map((c, i) => {
                  const col = colorFor(i + 1, false);
                  const desc = creatividadDesc(c, t, i18n.language);
                  return (
                    <article
                      key={c.id_creatividad}
                      className={styles.creatividadCard}
                      style={{ borderColor: col.ring, backgroundColor: col.soft }}
                    >
                      <div className={styles.creatCardHeader}>
                        <CreatividadNombre creatividad={c} variant="card" />
                        <span className={styles.creatCardPct} style={{ color: col.bar }}>
                          {c.porcentaje}%
                        </span>
                      </div>
                      <div className={styles.creatCardBarBg}>
                        <div
                          className={styles.creatCardBarFill}
                          style={{ width: `${c.porcentaje}%`, backgroundColor: col.bar }}
                        />
                      </div>
                      {desc && <p className={styles.creatCardDesc}>{desc}</p>}
                    </article>
                  );
                })}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

export default ResultadosEncuesta;
